"use client";

import logger from '../../utils/logger';
import { useEffect, useState, useRef } from "react";
import { usePubSub, useMeeting } from "@videosdk.live/react-sdk";
import { useSiteContext } from "../../context/SiteContext";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import useIsMobile from "../../hooks/useIsMobile";

/**
 * WebViewer component for loading and annotating HTML pages
 * Based on Apryse WebViewer HTML documentation: https://docs.apryse.com/web/guides/html/load-html
 *
 * REQUIRED SETUP:
 * 1. Install the HTML viewer package:
 *    npm install @pdftron/webviewer-html
 *
 * 2. Install and run the proxy server (required for CORS):
 *    npm install @pdftron/webviewer-html-proxy-server
 *
 *    In your server code:
 *    const HTMLProxyServer = require('@pdftron/webviewer-html-proxy-server');
 *    HTMLProxyServer.createServer({
 *      SERVER_ROOT: 'http://localhost',
 *      PORT: 3100
 *    });
 *
 * @param {string} htmlUrl - URL of the HTML page to load (default: "https://www.facebook.com")
 * @param {string} htmlProxyServerUrl - URL of the HTML proxy server (default: http://localhost:3100)
 * @param {function} onClosePanel - Callback function when panel is closed
 * @param {number} sideBarContainerWidth - Width of sidebar container for layout calculation
 * @param {string} licenseKey - Apryse license key (optional, uses default if not provided)
 */
export default function WebViewer({
  htmlUrl = "https://www.facebook.com",
  htmlProxyServerUrl = "http://localhost:3100",
  onClosePanel,
  sideBarContainerWidth,
  licenseKey,
}) {
  const { sideBarMode } = useMeetingAppContext();
  const { meetingRole } = useSiteContext();
  const isMobile = useIsMobile();

  const viewer = useRef(null);
  const webviewerInstanceRef = useRef(null);
  const [webviewerInstance, setWebviewerInstance] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [proxyLoaded, setProxyLoaded] = useState(false);

  const mMeeting = useMeeting();

  // Default license key (should be replaced with your actual key)
  const defaultLicenseKey =
    licenseKey ||
    "R-Link LLC:OEM:a5jOK000000xArN::B+3:AMS(20260828):A773575F761D1145BB535788D2C8F496839C21EDF969F948792B170966FA656D";

  useEffect(() => {
    // Use facebook.com as default if no URL provided
    const urlToLoad = htmlUrl || "https://www.facebook.com";

    if (!urlToLoad) {
      setError("HTML URL is required");
      setIsLoading(false);
      return;
    }

    let mounted = true;

    const initializeWebViewer = async () => {
      // Ensure viewer ref is available
      if (!viewer.current) {
        logger("warn", "Viewer ref not available, will retry...");
        setTimeout(() => initializeWebViewer(), 200);
        return;
      }
      try {
        logger("log", "🚀 Initializing WebViewer HTML for:", urlToLoad);
        setIsLoading(true);
        setError(null);

        // Dynamic import to avoid SSR issues
        logger("log", "📦 Loading WebViewer modules...");
        const [webviewerModule, htmlModule] = await Promise.all([
          import("@pdftron/webviewer"),
          import("@pdftron/webviewer-html").catch((err) => {
            logger("warn", 
              "@pdftron/webviewer-html not installed. Please install it: npm install @pdftron/webviewer-html",
              err
            );
            return null;
          }),
        ]);

        if (!htmlModule) {
          throw new Error(
            "@pdftron/webviewer-html module is not installed. Please run: npm install @pdftron/webviewer-html"
          );
        }

        const WebViewer = webviewerModule.default;
        const { initializeHTMLViewer } = htmlModule;

        // Step 1: Initialize WebViewer
        logger("log", "🔧 Initializing WebViewer instance...");
        if (!viewer.current) {
          throw new Error("Viewer container element is not available");
        }

        const instance = await WebViewer(
          {
            path: "/lib/webviewer",
            // licenseKey: defaultLicenseKey,
            disableVirtualDisplayMode: true, // Required for v8.5+
            enableAnnotations: true,
            enableFilePicker: false,
          },
          viewer.current
        );

        if (!mounted) return;

        logger("log", "✅ WebViewer instance created");
        webviewerInstanceRef.current = instance;
        setWebviewerInstance(instance);

        // Step 2: Get proxy URL from proxy server
        logger("log", `🌐 Connecting to proxy server: ${htmlProxyServerUrl}`);
        const encodedUrl = encodeURIComponent(urlToLoad);
        const proxyRequestUrl = `${htmlProxyServerUrl}/pdftron-proxy?url=${encodedUrl}`;
        logger("log", "📡 Proxy request URL:", proxyRequestUrl);

        let proxyResponse;
        try {
          proxyResponse = await fetch(proxyRequestUrl, {
            credentials: "include", // Important for cross-origin requests
            mode: "cors",
          });

          if (!proxyResponse.ok) {
            const errorText = await proxyResponse
              .text()
              .catch(() => "Unknown error");
            throw new Error(
              `Proxy server error: ${proxyResponse.status} ${proxyResponse.statusText}. ${errorText}`
            );
          }
        } catch (fetchError) {
          logger("error", "❌ Error fetching proxy URL:", fetchError);
          throw new Error(
            `Failed to connect to proxy server at ${htmlProxyServerUrl}. Error: ${fetchError.message}. Make sure the proxy server is running (npm run proxy).`
          );
        }

        const proxyData = await proxyResponse.json();
        logger("log", "📥 Proxy server response:", proxyData);

        if (!proxyData || !proxyData.validUrl) {
          throw new Error(
            `Proxy server did not return a valid URL. Response: ${JSON.stringify(
              proxyData
            )}`
          );
        }

        const { validUrl } = proxyData;

        // Step 3: Parse the proxy URL to get the path
        let hrefWithoutOrigin;
        try {
          const { href, origin, pathname } = new URL(validUrl);
          hrefWithoutOrigin = href.split(origin)[1] || pathname;
          logger("log", "🔗 Parsed proxy URL path:", hrefWithoutOrigin);
        } catch (urlError) {
          throw new Error(
            `Invalid URL from proxy server: ${validUrl}. Error: ${urlError.message}`
          );
        }

        // Step 4: Initialize HTML viewer
        logger("log", "🎨 Initializing HTML viewer...");
        const { loadHTMLPage } = await initializeHTMLViewer(instance, {
          license: defaultLicenseKey,
        });

        // Step 5: Load the HTML page
        const containerRect = viewer.current?.getBoundingClientRect();
        const width = containerRect?.width || 1440;
        const height = containerRect?.height || 770;

        logger("log", `📄 Loading HTML page with dimensions: ${width}x${height}`);
        logger("log", 
          "📍 Iframe URL:",
          `${htmlProxyServerUrl}${hrefWithoutOrigin}`
        );
        logger("log", "🌍 URL to proxy:", validUrl);

        await loadHTMLPage({
          iframeUrl: `${htmlProxyServerUrl}${hrefWithoutOrigin}`,
          urlToProxy: validUrl,
          width,
          height,
        });

        if (!mounted) return;

        logger("log", "⏳ Waiting for proxy to load...");

        // Listen for proxy loaded event
        const { documentViewer } = instance.Core;
        const proxyLoadedHandler = () => {
          logger("log", "✅ Proxy loaded successfully!");
          setProxyLoaded(true);
          setIsLoading(false);
        };

        // Also set a timeout in case the event doesn't fire
        const timeoutId = setTimeout(() => {
          if (!proxyLoaded) {
            logger("warn", "⚠️ Proxy loaded event timeout, assuming loaded");
            setProxyLoaded(true);
            setIsLoading(false);
          }
        }, 10000); // 10 second timeout

        documentViewer.addEventListener("proxyLoaded", proxyLoadedHandler);

        // Cleanup listener on unmount
        return () => {
          clearTimeout(timeoutId);
          documentViewer.removeEventListener("proxyLoaded", proxyLoadedHandler);
        };
      } catch (err) {
        logger("error", "❌ Error initializing WebViewer HTML:", err);
        logger("error", "Error details:", {
          message: err.message,
          stack: err.stack,
          url: urlToLoad,
          proxyServer: htmlProxyServerUrl,
        });
        if (mounted) {
          setError(err.message || "Failed to load HTML page");
          setIsLoading(false);
        }
      }
    };

    // Initialize after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      initializeWebViewer();
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      mounted = false;
      // Cleanup WebViewer instance if needed
      if (webviewerInstanceRef.current) {
        // WebViewer cleanup will be handled by component unmount
        webviewerInstanceRef.current = null;
      }
    };
  }, [htmlUrl, htmlProxyServerUrl, defaultLicenseKey]);

  // Handle window resize
  useEffect(() => {
    if (!webviewerInstance || !proxyLoaded) return;

    const handleResize = () => {
      // WebViewer should handle resize automatically, but we can trigger a refresh if needed
      if (webviewerInstance?.UI) {
        webviewerInstance.UI.setLayoutMode(
          webviewerInstance.UI.getLayoutMode()
        );
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [webviewerInstance, proxyLoaded]);

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <div className="text-center p-4">
          <div className="text-red-600 font-semibold mb-2">
            Error Loading HTML
          </div>
          <div className="text-sm text-gray-600 mb-4">{error}</div>
          {error.includes("proxy server") && (
            <div className="text-xs text-gray-500 mt-4 space-y-2">
              <p className="font-semibold">
                Please ensure the HTML proxy server is running:
              </p>
              <div className="space-y-1">
                <code className="bg-gray-200 px-2 py-1 rounded block">
                  npm run proxy
                </code>
                <p className="text-xs mt-2">Or in a separate terminal:</p>
                <code className="bg-gray-200 px-2 py-1 rounded block">
                  node proxy-server.js
                </code>
              </div>
              <p className="text-xs mt-2">
                The server should be running on {htmlProxyServerUrl}
              </p>
            </div>
          )}
          {onClosePanel && (
            <button
              onClick={onClosePanel}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Close
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full h-full relative"
      style={{
        width: sideBarMode ? `calc(100% - ${sideBarContainerWidth}px)` : "100%",
      }}
    >
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <div className="text-gray-600">Loading HTML page...</div>
            {!proxyLoaded && (
              <div className="text-sm text-gray-500 mt-2">
                Connecting to proxy server...
              </div>
            )}
          </div>
        </div>
      )}

      <div
        ref={viewer}
        className="w-full h-full"
        style={{
          minHeight: isMobile ? "400px" : "600px",
          width: "100%",
          height: "100%",
        }}
      />
    </div>
  );
}
